/**
 * BUNDLE PICKER
 *
 * Set-/Mengenauswahl in der Buy-Box, als Ersatz fuer die Kaching-Bundles-App.
 *
 * Bewusst minimalinvasiv: die Komponente steuert ausschliesslich die beiden Standardfelder
 * des Shopify-Produktformulars - `id` (Variante) und `quantity` (Menge). Sie faengt weder
 * den Submit ab noch schreibt sie selbst in den Warenkorb. Dadurch bleiben Cart-Drawer,
 * Sticky-ATC und alle Express-Checkout-Buttons (PayPal, Shop Pay, Klarna) unveraendert.
 *
 * Der Rabatt der Mengen-Bar wird NICHT hier vergeben, sondern von einem automatischen
 * Rabatt in Shopify. Diese Datei zeigt ihn nur an.
 */
(() => {
  if (window.customElements.get('bundle-picker')) return;

  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;

      this.quantityInput = this._resolveQuantityInput();
      this.variants = this._readVariants();
      this.previousVariant = this._findVariant(this._currentVariantId());

      this._onChange = this._onChange.bind(this);
      this.addEventListener('change', this._onChange);

      // Beim Laden nur die Felder setzen - ohne Event, sonst springt die Galerie.
      this._apply({ silent: true });
    }

    _readVariants() {
      const node = this.querySelector('[data-bp-variants]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent) || [];
      } catch (e) {
        return [];
      }
    }

    _currentVariantId() {
      const idInput = this.form.querySelector('input[name="id"], select[name="id"]');
      return idInput ? parseInt(idInput.value, 10) : null;
    }

    _findVariant(id) {
      return this.variants.find(function (v) { return v.id === id; }) || null;
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }

    _onChange(event) {
      if (event.target.matches('[data-bp-variant-select]')) {
        // Auswahl in einem Dropdown waehlt implizit die zugehoerige Zeile
        const bar = event.target.closest('.bundle-picker__bar');
        const radio = bar && bar.querySelector('.bundle-picker__input');
        if (radio) {
          radio.checked = true;
          this._refreshQuantityBarPricing(radio);
        }
      }
      this._apply();
    }

    /**
     * Nutzt ein vorhandenes Mengenfeld (Block "quantity_selector"), sonst wird ein
     * verstecktes angelegt. So entsteht nie ein zweites, widersprechendes Mengenfeld.
     */
    _resolveQuantityInput() {
      const existing =
        this.form.querySelector('input[name="quantity"]') ||
        document.querySelector('input[name="quantity"][form="' + this.dataset.formId + '"]');

      if (existing) return existing;

      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = '1';
      this.form.appendChild(input);
      return input;
    }

    /**
     * Liest die pro Stueck gewaehlten Varianten einer Zeile.
     * Ohne Auswahlfelder (Zeilen mit fester Variante) ist es schlicht n-mal dieselbe.
     */
    _selectedVariantIds(radio) {
      const bar = radio.closest('.bundle-picker__bar');
      const selects = bar ? bar.querySelectorAll('[data-bp-variant-select]') : [];
      const quantity = parseInt(radio.dataset.quantity, 10) || 1;

      if (!selects.length) {
        const single = parseInt(radio.dataset.variantId, 10);
        return new Array(quantity).fill(single);
      }

      return Array.from(selects).map(function (s) { return parseInt(s.value, 10); });
    }

    /**
     * Rechnet Preis und Streichpreis der Mengen-Zeile aus den gewaehlten Varianten neu.
     * Bei gemischter Wahl ist das die Summe der Einzelpreise, nicht Menge x Einzelpreis.
     */
    _refreshQuantityBarPricing(radio) {
      const bar = radio.closest('.bundle-picker__bar');
      const selects = bar ? bar.querySelectorAll('[data-bp-variant-select]') : [];
      if (!selects.length) return;

      const discount = parseFloat(radio.dataset.discount) || 0;
      let total = 0;
      let compareTotal = 0;

      Array.from(selects).forEach(function (s) {
        const option = s.options[s.selectedIndex];
        if (!option) return;
        const price = parseInt(option.dataset.price, 10) || 0;
        total += price;
        compareTotal += parseInt(option.dataset.compare, 10) || price;
      });

      const final = Math.round(total - (total * discount) / 100);
      const container = radio.nextElementSibling;
      const priceEl = container.querySelector('[data-bp-price]');
      const compareEl = container.querySelector('[data-bp-compare]');

      if (priceEl) priceEl.textContent = this._formatMoney(final);
      if (compareEl) {
        compareEl.textContent = this._formatMoney(compareTotal);
        compareEl.hidden = compareTotal <= final;
      }
    }

    /**
     * Schreibt die Warenkorbfelder.
     *
     * Alle Stuecke dieselbe Variante -> `id` + `quantity`. Das ist der Standardweg,
     * bei dem auch die Express-Checkout-Buttons funktionieren.
     *
     * Gemischt -> `items[n][id]` / `items[n][quantity]`. Dieses Format nimmt
     * /cart/add.js entgegen, und das Theme verarbeitet die Mehrfach-Antwort bereits
     * (siehe ProductForm._onSubmit in theme.js). `id` und `quantity` werden dabei
     * deaktiviert, damit nicht beide Formate gleichzeitig gesendet werden.
     */
    _writeCartFields(variantIds) {
      const idInput = this.form.querySelector('input[name="id"], select[name="id"]');
      const first = variantIds[0];
      const mixed = variantIds.some(function (id) { return id !== first; });

      this._clearItemFields();

      if (mixed) {
        if (idInput) idInput.disabled = true;
        if (this.quantityInput) this.quantityInput.disabled = true;

        // Gleiche Varianten zu einer Position zusammenfassen
        const counts = new Map();
        variantIds.forEach(function (id) { counts.set(id, (counts.get(id) || 0) + 1); });

        let index = 0;
        const self = this;
        counts.forEach(function (quantity, id) {
          self._addItemField('items[' + index + '][id]', id);
          self._addItemField('items[' + index + '][quantity]', quantity);
          index++;
        });
      } else {
        if (idInput) {
          idInput.disabled = false;
          idInput.value = first;
        }
        if (this.quantityInput) {
          this.quantityInput.disabled = false;
          this.quantityInput.value = variantIds.length;
        }
      }

      this.form.classList.toggle('bundle-picker-mixed', mixed);
      return mixed;
    }

    _addItemField(name, value) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      input.dataset.bpItemField = 'true';
      this.form.appendChild(input);
    }

    _clearItemFields() {
      this.form.querySelectorAll('[data-bp-item-field]').forEach(function (el) { el.remove(); });
    }

    _apply(options) {
      const silent = options && options.silent;
      const selected = this.querySelector('.bundle-picker__input:checked');
      if (!selected) return;

      const variantIds = this._selectedVariantIds(selected);
      this._writeCartFields(variantIds);

      if (silent) return;

      const variant = this._findVariant(variantIds[0]);
      if (!variant) return;

      this._updateThemePrice(this._totals(selected, variantIds));
      this.previousVariant = variant;
    }

    /**
     * Summiert die gewaehlten Varianten und zieht den Rabatt der Zeile ab.
     * Bei gemischter Wahl ist das die Summe der Einzelpreise, nicht Menge x Preis.
     */
    _totals(radio, variantIds) {
      const discount = parseFloat(radio.dataset.discount) || 0;
      let total = 0;
      let compare = 0;

      const self = this;
      variantIds.forEach(function (id) {
        const v = self._findVariant(id);
        if (!v) return;
        total += v.price;
        compare += v.compare_at_price || v.price;
      });

      return { final: Math.round(total - (total * discount) / 100), compare: compare };
    }

    /**
     * Schreibt den Preis der gewaehlten Zeile in den Preis-Block des Themes.
     *
     * Dawn rendert die Produkt-Section bei einem nativen Variantenwechsel neu; deshalb
     * steuert diese Komponente den nativen Picker NICHT an, sondern setzt den Preis
     * selbst. Findet sie die Struktur nicht, passiert nichts - der Kaufweg haengt
     * nicht daran.
     */
    _updateThemePrice(totals) {
      const root = document.querySelector('.price');
      if (!root) return;

      const imAngebot = totals.compare > totals.final;
      root.classList.toggle('price--on-sale', imAngebot);

      const regular = root.querySelector('.price__regular .price-item--regular');
      if (regular) regular.textContent = ' ' + this._formatMoney(totals.final, true) + ' ';

      const streich = root.querySelector('.price__sale s.price-item--regular');
      if (streich) streich.textContent = ' ' + this._formatMoney(totals.compare, true) + ' ';

      const aktuell = root.querySelector('.price__sale .price-item--sale');
      if (aktuell) aktuell.textContent = ' ' + this._formatMoney(totals.final, true) + ' ';
    }

    /**
     * Bildet das Geldformat des Shops nach. Deutsche Notation: 1.234,56
     * mitWaehrung waehlt das Format mit Waehrungskuerzel (fuer den Preis-Block des Themes).
     */
    _formatMoney(cents, mitWaehrung) {
      const format = mitWaehrung
        ? this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat || '€{{amount_with_comma_separator}} EUR'
        : this.dataset.moneyFormat || '€{{amount_with_comma_separator}}';
      const amount = (cents / 100).toFixed(2);
      const parts = amount.split('.');
      const whole = parts[0];
      const decimal = parts[1];

      const groupedComma = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      const groupedPeriod = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

      return format
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, groupedComma + ',' + decimal)
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, groupedComma)
        .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, whole.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + '.' + decimal)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, groupedPeriod)
        .replace(/\{\{\s*amount\s*\}\}/g, groupedPeriod + '.' + decimal);
    }
  }

  window.customElements.define('bundle-picker', BundlePicker);
})();
